import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import SplitText from 'gsap/dist/SplitText'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Grid, { GridProps } from '@/components/base/Grid'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'
import { SectionHandler } from '@/components/sections/HeroSection'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ContentProps = GridProps & {
  title: string
  variant: string
  paragraph: string
  button?: WPLink
}

const Content = forwardRef<SectionHandler, ContentProps>(
  ({ title, paragraph, button, variant, children, ...props }, ref) => {
    const root = useRef<HTMLDivElement>(null)

    const { context, contextSafe } = useGSAP(() => {}, { scope: root })

    const splitTitle = useRef<SplitText>(null!)
    const splitParagraph = useRef<SplitText>(null!)

    const createTimeline = contextSafe(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: { ease: 'none' }
      })

      return timeline
    })

    const createEnterTimeline = contextSafe(() => {
      const title = context.selector?.('[data-motion-title]')
      const paragraph = context.selector?.('[data-motion-paragraph]')
      const button = context.selector?.('[data-motion-button]')
      splitTitle.current = SplitText.create(title, { type: 'words', wordsClass: 'word' })
      splitParagraph.current = SplitText.create(paragraph, { type: 'lines' })

      const timeline = gsap.timeline({
        paused: true,
        defaults: { duration: 1, ease: 'power3.inOut', stagger: { amount: 0.7 } }
      })

      timeline
        .from(splitTitle.current.words, { opacity: 0.1 })
        .from(splitParagraph.current.lines, { opacity: 0.1 }, 0.3)

      if (button) {
        timeline.from(button, { opacity: 0 }, 0.5)
      }

      return timeline
    })

    const createLeaveTimeline = contextSafe(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: {
          duration: 1,
          ease: 'power3.inOut',
          immediateRender: false,
          stagger: { amount: 0.5 }
        }
      })

      timeline
        .to(splitTitle.current.words, { opacity: 0.1 })
        .to(splitParagraph.current.lines, { opacity: 0.1 }, 0.3)

      return timeline
    })

    const resetTimeline = contextSafe(() => {
      splitTitle.current && splitTitle.current.revert()
      splitParagraph.current && splitParagraph.current.revert()
    })

    useImperativeHandle(
      ref,
      () => ({
        createTimeline,
        createEnterTimeline,
        createLeaveTimeline,
        resetTimeline
      }),
      [createTimeline, createEnterTimeline, createLeaveTimeline, resetTimeline]
    )

    return (
      <Grid
        ref={root}
        className="relative gap-y-[11.2rem] px-[2rem] md:px-0 lg:gap-y-[19.5rem]"
        {...props}
      >
        <div
          className={cn([
            'col-span-4 md:col-span-4 lg:col-span-4',
            {
              'md:col-start-2 lg:col-start-2': 'hero' === variant,
              'md:col-start-2 lg:col-span-5 lg:col-start-4 xl:col-span-4 xl:col-start-4':
                'chip' === variant,
              'md:col-start-2 lg:col-span-5 lg:col-start-7 xl:col-span-4 xl:col-start-8':
                'neutrino' === variant,
              'md:col-start-2 lg:col-span-5 lg:col-start-5 xl:col-span-4 xl:col-start-5':
                'tunnel' === variant
            }
          ])}
        >
          <div className="-my-[1em] flex overflow-hidden py-[1em]">
            <Typography
              variant="h3"
              component="h2"
              className="text-text-accent-light [&_.word]:whitespace-nowrap"
              data-motion-title
            >
              {title}
            </Typography>
          </div>
        </div>
        <div
          className={cn([
            'col-span-4 md:col-span-4 lg:col-span-7',
            'grid grid-cols-4 gap-x-gutter md:grid-cols-4 lg:grid-cols-7',
            {
              'md:col-start-2 lg:col-start-5': 'hero' === variant || 'tunnel' === variant,
              'md:col-start-2 lg:col-start-5 2xl:col-start-6': 'neutrino' === variant,
              'md:col-start-2 lg:col-start-4': 'chip' === variant
            }
          ])}
        >
          <div className="col-span-4 md:col-span-4 lg:col-span-7 2xl:lg:col-span-6">
            <div className="-my-[1em] flex overflow-hidden py-[1em]">
              <Typography
                variant="large"
                paragraph
                className="font-medium text-text-primary-dark"
                data-motion-paragraph
              >
                {paragraph}
              </Typography>
            </div>
            {button?.title ? (
              <div className="mt-spacing-xs inline-block">
                <Button
                  asChild
                  className="justify-start pl-0"
                  variant="secondary"
                  data-motion-button
                  endIcon={
                    <SVGIcon
                      className="h-[1.8rem] w-[1.6rem] fill-surface-primary-dark"
                      symbol="arrow-right-2"
                    />
                  }
                >
                  <Link href={button!.url} target={button?.target}>
                    <TypographyMask>
                      <Typography variant="regular">{button?.title}</Typography>
                    </TypographyMask>
                  </Link>
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {children}
      </Grid>
    )
  }
)

export default Content
